import { SelectedPaymentMethod } from 'hooks/useSelectedPaymentMethod';
import {
  parkingPaymentCardId,
  parkingVehicleLicensePlate,
} from 'states/common';
import { ParkingZoneType } from 'types/common';
import {
  CountryCode,
  CreateAnprParkingSessionInput,
  ResumableParkingInfo,
  SegmentProps,
} from 'types/generatedSchemaTypes';
import { OngoingParkingStateProps } from 'types/states';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { zustandMMKVStorage } from './utils/commonStoreUtils';

type ParkingConditions = {
  selectedParkingZoneId: string;
  parkingZoneCountryCode: CountryCode | undefined;
  paymentMethod: SelectedPaymentMethod | undefined;
  endTime: Date | undefined;
  isValidEndTime: boolean;
};

// Parking-session domain state migrated from Apollo reactive vars (CPS-26938).
// These five session-ish vars are deliberately distinct — do not conflate them:
// - ongoingParkingSession: the live active-session data object (drives ongoing
//   UI, timers, stop action). Set from the readOngoingParkingSession query.
// - selectedOngoingSession: a TYPE discriminator (parking vs charging), NOT a
//   session id — picks which ongoing sheet/header is shown. Settable to
//   undefined by consumers, so the type is `ParkingZoneType | undefined`.
// - selectedSessionIdFromMultiple: sessionId chosen among several concurrent
//   ongoing sessions.
// - selectedParkingSession: sessionId currently viewed/edited in history.
// - currentParkingSession: sessionId of a just-created prepaid session.
type ParkingSessionState = {
  ongoingParkingSession: OngoingParkingStateProps;
  selectedOngoingSession: ParkingZoneType | undefined;
  selectedSessionIdFromMultiple: string | null;
  // Observability flow ids keyed by parking session id. Keyed rather than a
  // single slot because several useCreateSession instances can be mounted at
  // once — with one shared slot the last mount won and the stop/extend flow
  // logged under another component's flow id.
  parkingSessionFlowIds: Record<string, string>;
  selectedParkingSession: string | null;
  currentParkingSession: string | null;
  resumableSession: ResumableParkingInfo | undefined;
  resumeSessionPossible: boolean;
  anprParkingInput: CreateAnprParkingSessionInput | null;
};

type ParkingSessionActions = {
  setOngoingParkingSession: (session: OngoingParkingStateProps) => void;
  setSelectedOngoingSession: (type: ParkingZoneType | undefined) => void;
  setSelectedSessionIdFromMultiple: (sessionId: string | null) => void;
  setParkingSessionFlowId: (sessionId: string, flowId: string) => void;
  clearParkingSessionFlowId: (sessionId: string) => void;
  setSelectedParkingSession: (sessionId: string | null) => void;
  setCurrentParkingSession: (sessionId: string | null) => void;
  setResumableSession: (session: ResumableParkingInfo | undefined) => void;
  setResumeSessionPossible: (possible: boolean) => void;
  setAnprParkingInput: (input: CreateAnprParkingSessionInput | null) => void;
};

type ParkingState = {
  parkingConditions: ParkingConditions;
  selectedParkingVehiclePlate?: string;
  currentPaymentMethodId?: string;
  selectedParkingSegment: SegmentProps | undefined;
  // True once the user has manually picked a parking tariff for the current
  // zone. Guards the auto-selection in useTariffs so a manual choice isn't
  // overwritten when the effect re-runs (e.g. navigating to the scroll wheel).
  // SET by: SelectTariffModalContent + UnlockTariffModalContent (manual picks).
  // CLEARED by: setParkingZoneReactiveVars (map/search zone change) and
  // ParkingZoneDetails focus effect (route-param zone change). Any new
  // zone-entry path must also clear it or the new zone won't pre-select.
  hasManuallySelectedTariff: boolean;
} & ParkingSessionState;

type ParkingActions = {
  setParkingConditions: (conditions: Partial<ParkingConditions>) => void;
  setParkingVehiclePlate: (vehicleId?: string) => void;
  setSelectedParkingSegment: (segment?: SegmentProps) => void;
  setCurrentPaymentMethodId: (cardId?: string) => void;
  setHasManuallySelectedTariff: (value: boolean) => void;
  resetParkingState: () => void;
} & ParkingSessionActions;

// Define initial state
const initialParkingConditions: ParkingConditions = {
  selectedParkingZoneId: '',
  parkingZoneCountryCode: undefined,
  paymentMethod: undefined,
  endTime: undefined,
  isValidEndTime: true,
};

// NOTE: initialState intentionally excludes the migrated session vars. It is
// used by resetParkingState's `set(initialState)`, and zustand's `set` merges
// shallowly — so the session vars are left untouched by a reset, exactly as the
// reactive vars they replace were (resetParkingState never cleared them). Each
// session var keeps its own explicit clear points in the consumer code.
const initialState: Omit<ParkingState, keyof ParkingSessionState> = {
  parkingConditions: initialParkingConditions,
  selectedParkingVehiclePlate: undefined,
  currentPaymentMethodId: undefined,
  selectedParkingSegment: undefined,
  hasManuallySelectedTariff: false,
};

// Initial values for the migrated session vars — identical to the reactive-var
// defaults in states/common.ts prior to CPS-26938.
const initialParkingSessionState: ParkingSessionState = {
  ongoingParkingSession: {
    sessionId: '',
    vehicleLabel: '',
    endTime: undefined,
    parkingSessionType: undefined,
  },
  selectedOngoingSession: '',
  selectedSessionIdFromMultiple: null,
  parkingSessionFlowIds: {},
  selectedParkingSession: null,
  currentParkingSession: null,
  resumableSession: undefined,
  resumeSessionPossible: false,
  anprParkingInput: null,
};
export const useParkingStore = create<ParkingState & ParkingActions>()(
  persist(
    (set, get) => ({
      ...initialState,
      ...initialParkingSessionState,

      setOngoingParkingSession: (session) =>
        set({ ongoingParkingSession: session }),
      setSelectedOngoingSession: (type) =>
        set({ selectedOngoingSession: type }),
      setSelectedSessionIdFromMultiple: (sessionId) =>
        set({ selectedSessionIdFromMultiple: sessionId }),
      setParkingSessionFlowId: (sessionId, flowId) =>
        set({
          parkingSessionFlowIds: {
            ...get().parkingSessionFlowIds,
            [sessionId]: flowId,
          },
        }),
      clearParkingSessionFlowId: (sessionId) => {
        const { [sessionId]: _removed, ...rest } = get().parkingSessionFlowIds;
        set({ parkingSessionFlowIds: rest });
      },
      setSelectedParkingSession: (sessionId) =>
        set({ selectedParkingSession: sessionId }),
      setCurrentParkingSession: (sessionId) =>
        set({ currentParkingSession: sessionId }),
      setResumableSession: (session) => set({ resumableSession: session }),
      setResumeSessionPossible: (possible) =>
        set({ resumeSessionPossible: possible }),
      setAnprParkingInput: (input) => set({ anprParkingInput: input }),

      setParkingConditions: (conditions: Partial<ParkingConditions>) => {
        const currentConditions = get().parkingConditions;
        set({
          parkingConditions: {
            ...currentConditions,
            ...conditions,
          },
        });
      },
      setParkingVehiclePlate: (vehicleId?: string) => {
        set({ selectedParkingVehiclePlate: vehicleId });
      },
      setSelectedParkingSegment: (segment?: SegmentProps) => {
        set({ selectedParkingSegment: segment });
      },
      setCurrentPaymentMethodId: (cardId?: string) => {
        set({ currentPaymentMethodId: cardId });
      },
      setHasManuallySelectedTariff: (value: boolean) => {
        set({ hasManuallySelectedTariff: value });
      },
      resetParkingState: () => {
        useParkingStore.persist.clearStorage();
        parkingVehicleLicensePlate(undefined);
        parkingPaymentCardId(undefined);
        set(initialState);
      },
    }),
    {
      name: 'parking-store',
      storage: createJSONStorage(() => zustandMMKVStorage),
      partialize: (state) => ({
        parkingConditions: {
          selectedParkingZoneId: state.parkingConditions.selectedParkingZoneId,
        },
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<ParkingState>;
        return {
          ...currentState,
          parkingConditions: {
            ...currentState.parkingConditions,
            ...persisted.parkingConditions,
          },
        };
      },
    }
  )
);
